import { Box, Divider, Stack, Typography } from "@mui/material"
import { CSSProperties, useCallback, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { selectSelectedLocale } from "../../../../../localization/localizationSelectors"
import { useAppSelector } from "../../../../../store/storeHooks"

import { HeroStat } from "./HeroStat"
import { TenantUsageDonut } from "./TenantUsageDonut"
import { UsageCard } from "./UsageCard"
import type { TenantUsageSummaryViewModel } from "./tenantUsageViewModel"

const useStyles = makeStyles()((theme) => ({
    donutColumn: {
        flex: "0 0 auto",
    },
    legendColumn: {
        flex: "1 1 20rem",
    },
    legendRow: {
        padding: ".375rem .5rem",
        borderRadius: ".5rem",
        transition: "background-color .15s ease",
    },
    legendRowActive: {
        backgroundColor: theme.palette.tailwind.slate[100],
    },
    swatch: {
        flex: "0 0 auto",
        width: ".625rem",
        height: ".625rem",
        borderRadius: ".125rem",
        backgroundColor: "var(--sw-color)",
    },
    legendName: {
        flexGrow: 1,
        color: theme.palette.tailwind.slate[700],
    },
    legendPercent: {
        flex: "0 0 auto",
        color: theme.palette.tailwind.slate[500],
    },
    legendTextActive: {
        fontWeight: 700,
        color: theme.palette.tailwind.slate[900],
    },
    divider: {
        margin: "1.5rem 0",
    },
    empty: {
        padding: "2rem",
        textAlign: "center",
        color: theme.palette.tailwind.slate[500],
    },
}))

type TenantUsageSummaryProps = Readonly<{
    summary: TenantUsageSummaryViewModel
}>

export function TenantUsageSummary({ summary }: TenantUsageSummaryProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const locale = useAppSelector(selectSelectedLocale)
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

    const handleHoverIndex = useCallback((index: number | null) => {
        setHoveredIndex((prev) => (prev === index ? prev : index))
    }, [])

    const numberFormat = useMemo(() => new Intl.NumberFormat(locale), [locale])

    const centerValue = l10n.getString("service-center-usage-per-tenant-summary-hours", {
        hours: numberFormat.format(summary.totalHoursBooked),
    })

    if (summary.legend.length === 0) {
        return (
            <UsageCard>
                <LocalizedStrict id="service-center-usage-per-tenant-empty-tenants">
                    <Typography className={classes.empty}>No tenant activity for the selected filters.</Typography>
                </LocalizedStrict>
            </UsageCard>
        )
    }

    return (
        <UsageCard>
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap="2.5rem">
                <Box className={classes.donutColumn}>
                    <TenantUsageDonut
                        legend={summary.legend}
                        centerValue={centerValue}
                        hoveredIndex={hoveredIndex}
                        onHoverIndex={handleHoverIndex}
                    />
                </Box>
                <Stack className={classes.legendColumn}>
                    {summary.legend.map((item, index) => {
                        // CSS custom property for the swatch color (data-driven design token).
                        const swatchStyle = { "--sw-color": item.color } as CSSProperties

                        return (
                            <Stack
                                key={item.key}
                                direction="row"
                                alignItems="center"
                                gap=".75rem"
                                className={cx(classes.legendRow, index === hoveredIndex && classes.legendRowActive)}
                                onMouseEnter={() => handleHoverIndex(index)}
                                onMouseLeave={() => handleHoverIndex(null)}
                            >
                                <span className={classes.swatch} style={swatchStyle} />
                                {item.isOther ? (
                                    <LocalizedStrict
                                        id="service-center-usage-per-tenant-summary-legend-other"
                                        vars={{ count: item.aggregatedCount }}
                                    >
                                        <Typography
                                            variant="body2"
                                            className={cx(
                                                classes.legendName,
                                                index === hoveredIndex && classes.legendTextActive,
                                            )}
                                        >
                                            Other tenants
                                        </Typography>
                                    </LocalizedStrict>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        className={cx(
                                            classes.legendName,
                                            index === hoveredIndex && classes.legendTextActive,
                                        )}
                                    >
                                        {item.name}
                                    </Typography>
                                )}
                                <Typography
                                    variant="body2"
                                    className={cx(
                                        classes.legendPercent,
                                        index === hoveredIndex && classes.legendTextActive,
                                    )}
                                >
                                    {item.percentage}%
                                </Typography>
                            </Stack>
                        )
                    })}
                </Stack>
            </Stack>

            <Divider className={classes.divider} />

            <Stack direction="row" flexWrap="wrap" gap="4rem">
                <HeroStat
                    value={numberFormat.format(summary.totalResources)}
                    labelId="service-center-usage-per-tenant-summary-resources-tracked"
                />
                <HeroStat
                    value={numberFormat.format(summary.totalActiveTenants)}
                    labelId="service-center-usage-per-tenant-summary-tenants-active"
                />
            </Stack>
        </UsageCard>
    )
}
